import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { StyleSheet, View } from 'react-native';

import { redirectToTeacherDashboard } from 'lib/utilityFunctions';
import Env from 'lib/Env';
import AuthService from 'lib/authService';
import { registerLogoutListener, closeAuthChannel } from 'lib/authBroadcast';
import LoadingScreen, { ANIMATION_TYPE } from 'a/components/LoadingScreen/LoadingScreen';
import { fetchCurriculumVersions } from 'root/lib/CurriculumFetch';
import { setLocalizedThunk } from 'lib/l10n/l10n';
import { setRumQuestId, setRumProduct, logRumError, logRumAction } from 'lib/DDRum';
import { openInitialActivity, reportRumView } from 'a/hub/actions/metaGameActions';
import { getAllGamesState } from 'a/actions/getAllGamesState';
import {
  PRODUCT_NOT_YET_SELECTED,
  PRODUCT_LECTURA,
  PRODUCT_READING,
  PRODUCT_EXPERIENCES,
  LOCAL_STORAGE_TAB_ID,
  SESSION_STORAGE_TAB_ID,
} from 'root/lib/constants';
import ModalSystem from 'a/components/ModalSystem/ModalSystem';
import { fetchSessionProgress } from 'a/hub/actions/studentProgressActions';
import { getIsReportingMultipleSessions } from 'a/hub/selectors/sessionSelectors';
import {
  getIsModalOpen as getIsAquaModalOpen,
  getCurrentQuestId,
  getAreCaptionsAvailable,
} from 'root/activities/hub/selectors';
import { openLogoutModal, openMultipleSessionsModal } from 'a/hub/actions/modalSystemActions';
import { setProduct, setShowGamePrototypes } from 'a/actions/appSessionActions';
import { getAuthDataProductsAvailable } from 'a/selectors/persistStateSelectors';
import ProductSelectScreen from 'a/productSelection/ProductSelectScreen';
import studentLoginEventHandler from 'lib/studentLogin';
import handleDeepLinkParams from 'root/lib/DeeplinkParamsHandler';
import { fetchStaticAssetsManifest } from '../../lib/StaticAssetsManifest';
import GameLoader from './GameLoader';
import AppGlobalStyle from './AppGlobalStyle';
import GlobalErrorHandler from './GlobalErrorHandler/GlobalErrorHandler';
import SessionTimeout from './SessionTimeout';
import { LiveRegionContainer } from './LiveRegion';
import {
  updateTOY,
  getDemoData,
  fetchVocabResultsData,
  transformPlacementFields,
} from '../actions/persistStateActions';
import { getFeatureToggles } from '../actions/featureToggleActions';
import { fetchProductsWeeklyUsage, fetchUsageTargets } from '../actions/usageActions';
import { getIsClientA11yFeatures } from '../selectors/featureToggleSelectors';
import { setQuestSessionId, setInitialQuestStateForLectura } from '../hub/actions/questsActions';
import { fetchVocabStatus, fetchTFEConfig } from '../hub/actions/workshopActions';
import { fetchFluencyAssignments } from '../actions/fetchFluencyAssignments';
import { SEASON } from '../hub/data/constants';
import getLastInternetSpeed from '../../lib/InternetSpeedTest';
import globalStore from '../store';
import CustomErrorThrower from './CustomErrorThrower';
import Captions from './Captions';
import { setSkillScanRedirectFlag } from '../skillScan/redux/actions';
import { setBenchmarkModeRedirectFlag } from '../placementTool/api/apiEvents';
import Title from './Title';
import AmpGlobalNav from './AmpGlobalNav';
import getEMGames from '../api/embeddedMeasurementGames';

export class ActivityComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      canRenderMainContent: false,
    };

    this.demoContext = {};
  }

  async componentDidMount() {
    registerLogoutListener(() => AuthService.localLogout());

    const {
      apiVersionKeycloak,
      isStaffLogin,
      keycloakSessionId,
      token,
      deepLinkConfig,
      hasPrototypeParam,
    } = this.props;
    const isDeepLink = !!deepLinkConfig.product;
    await this.props.studentLoginEventHandler(
      apiVersionKeycloak,
      isStaffLogin,
      keycloakSessionId,
      token,
      deepLinkConfig,
    );

    this.props.reportAppLoaded();

    // On browser back button click
    window.addEventListener('popstate', () => {
      // If no error, show the logout modal and don't back to previous page
      if (!this.props.isGlobalError) {
        this.props.openLogoutModal({ origin: 'browserBackButton' });
      } else {
        // On global error, redirect to main url to avoid showing error page again
        window.location.href = window.location.origin;
      }
    }, false);

    window.addEventListener('touchmove', this.preventDefault, {
      passive: false,
    });

    const featureToggles = await this.props.getFeatureToggles();
    if (hasPrototypeParam && featureToggles?.allowPrototypePlaytesting) {
      this.props.setShowGamePrototypes(true);
    }

    const { demoProduct, demoSeason } = await this.props.getDemoData();

    const showRealTimeUsage = featureToggles?.showRealTimeUsage && !demoSeason && !isStaffLogin && !isDeepLink;

    if (showRealTimeUsage) {
      const products =
        this.props.productSelected !== PRODUCT_NOT_YET_SELECTED
          ? [this.props.productSelected]
          : this.props.productsAvailable;

      await Promise.allSettled([
        this.props.fetchProductsWeeklyUsage(products),
        this.props.fetchUsageTargets(),
      ]);
    }

    if (isStaffLogin && !isDeepLink && !demoSeason) {
      logRumError('Missing demo mode data', { demoProduct, demoSeason });
      redirectToTeacherDashboard();
      return;
    }

    // Product selection screen appears before we get any data
    // We want postpone rendering that screen until we know this is not Demo mode
    // In Demo mode user should use demoProduct field and go directly into Demo
    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({ canRenderMainContent: true });

    if (isDeepLink) {
      const isQuestInDemoMode = false;
      // override product selection
      this.props.setProduct(deepLinkConfig.product);
      await this.loadApp(deepLinkConfig.product, isQuestInDemoMode, isDeepLink);
      window.reactLoaded();
    } else if (demoSeason) {
      this.demoContext = {
        demoSeason,
        demoProduct,
        isQuestInDemoMode: true,
        isDeepLink: false,
      };
      await this.launchAppInDemoMode(this.demoContext);
    } else if (this.props.productsAvailable.length === 1) {
      this.props.setProduct(this.props.productsAvailable[0]);
      await this.loadApp(this.props.productsAvailable[0]);
    } else if (this.props.productSelected !== PRODUCT_NOT_YET_SELECTED) {
      await this.loadApp(this.props.productSelected);
    }

    // beforeunload event is not supported on iOS devices
    // check for multiple tab sessions if not using iOS
    if (!Env.deviceDetails.isIOS) {
      this.detectMultipleSessions();
    }
  }

  componentDidUpdate(prevProps) {
    const { currentQuestId, selectedActivity } = this.props;

    if (this.gameLoaderWrapperRef && prevProps.isModalOpen !== this.props.isModalOpen) {
      if (this.props.isModalOpen) {
        this.gameLoaderWrapperRef.setAttribute('inert', '');
      } else {
        this.gameLoaderWrapperRef.removeAttribute('inert');
      }
    }

    if (
      prevProps.isWriteResultInProgress !== this.props.isWriteResultInProgress ||
      prevProps.isReportingMultipleSessions !== this.props.isReportingMultipleSessions
    ) {
      if (this.props.isWriteResultInProgress || this.props.isReportingMultipleSessions) {
        this.screenWrapperRef.setAttribute('inert', '');
      } else {
        this.screenWrapperRef.removeAttribute('inert');
      }
    }

    if (prevProps.currentQuestId !== currentQuestId) {
      setRumQuestId(currentQuestId);
    }

    if (prevProps.selectedActivity !== selectedActivity) {
      this.props.reportRumView(selectedActivity);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('touchmove', this.preventDefault);
    window.removeEventListener('storage', this.handleTabIdStorage);
    window.removeEventListener('beforeunload', this.handleBeforeunload);
    closeAuthChannel();
  }

  get isProductSelectionAvailable() {
    return this.props.productsAvailable.length > 1 && this.props.productSelected === PRODUCT_NOT_YET_SELECTED;
  }

  handleProductSelection = async (product) => {
    const { demoSeason, isQuestInDemoMode, isDeepLink } = this.demoContext;
    await this.loadApp(product, isQuestInDemoMode, isDeepLink, demoSeason);
  };

  launchAppInDemoMode = async ({ demoSeason, isQuestInDemoMode, isDeepLink, ...demoData } = {}) => {
    const { productsAvailable, productSelected } = this.props;
    const demoProduct = demoData.demoProduct?.toLowerCase();

    logRumAction('launch Demo mode', { demoProduct, demoSeason });

    if (demoSeason === SEASON.GRADE_4_5) {
      this.props.setProduct(PRODUCT_READING);
      await this.loadApp(PRODUCT_READING, isQuestInDemoMode, isDeepLink, demoSeason);
      return;
    }

    if (productsAvailable.includes(demoProduct) && PRODUCT_EXPERIENCES.includes(demoProduct)) {
      this.props.setProduct(demoProduct);
      await this.loadApp(demoProduct, isQuestInDemoMode, isDeepLink, demoSeason);
      return;
    }

    if (!this.isProductSelectionAvailable) {
      const defaultProduct = productsAvailable?.[0] || PRODUCT_READING;
      const product = productsAvailable.includes(productSelected) ? productSelected : defaultProduct;

      this.props.setProduct(product);
      await this.loadApp(product, isQuestInDemoMode, isDeepLink, demoSeason);
    }
    // do nothing here and let ProductSelectScreen render
  };

  preventDefault = (e) => {
    if (e.cancelable) e.preventDefault();
  };

  handleTabIdStorage = () => {
    if (localStorage.getItem(LOCAL_STORAGE_TAB_ID) !== sessionStorage.getItem(SESSION_STORAGE_TAB_ID)) {
      this.props.openMultipleSessionsModal();
    }
  };

  handleBeforeunload = () => {
    localStorage.removeItem(LOCAL_STORAGE_TAB_ID);
  };

  detectMultipleSessions = () => {
    // Set unique id to local and session storage
    // to detect multiple sessions on the same device
    const officialTabId = new Date().toISOString();
    sessionStorage.setItem(SESSION_STORAGE_TAB_ID, officialTabId);
    const lsTabId = localStorage.getItem(LOCAL_STORAGE_TAB_ID);

    if (!lsTabId) {
      localStorage.setItem(LOCAL_STORAGE_TAB_ID, officialTabId);
    } else if (lsTabId !== officialTabId) {
      this.props.openMultipleSessionsModal();
    }

    window.addEventListener('storage', this.handleTabIdStorage);
    window.addEventListener('beforeunload', this.handleBeforeunload);
  };

  async loadApp(product, isQuestInDemoMode = false, isDeepLink = false, demoSeason = '') {
    // every path into the app funnels through here, including a page refresh that restores
    // the product from sessionStorage without dispatching setProduct
    setRumProduct(product);

    this.props.transformPlacementFields(product);
    if (product === PRODUCT_LECTURA) {
      this.props.setInitialQuestStateForLectura();
    }

    await Promise.all([
      getLastInternetSpeed(),
      fetchCurriculumVersions(),
      fetchStaticAssetsManifest(),
      this.props.getAllGamesState(),
    ]);

    // Season 3 APIs
    if (!isDeepLink && [this.props.season, demoSeason].includes(SEASON.GRADE_4_5)) {
      await Promise.all([
        this.props.fetchTFEConfig(),
        this.props.fetchVocabStatus(),
        this.props.fetchVocabResultsData(),
      ]);
    }

    if (isQuestInDemoMode || isDeepLink) {
      // default TOY in Demo mode and deep link
      this.props.updateTOY('BOY');
    } else {
      await this.props.setBenchmarkModeRedirectFlag();
      this.props.setSkillScanRedirectFlag();
    }

    if (!isDeepLink && !isQuestInDemoMode && !this.props.isStaffLogin) {
      await this.props.fetchFluencyAssignments();
    }

    // get EM games when not in demo mode and non-teacher deep link
    if (!isQuestInDemoMode && !this.props.isStaffLogin && product === PRODUCT_READING) {
      this.props.getEMGames();
    }

    this.props.fetchSessionProgress();

    this.props.setQuestSessionId();

    this.props.setLocalizedThunk();

    if (isDeepLink && await this.props.handleDeepLinkParams(this.props.deepLinkConfig)) {
      return;
    }

    this.props.openInitialActivity(product);
  }

  renderMainContent() {
    if (this.isProductSelectionAvailable) {
      return (
        <ProductSelectScreen
          onProductSelection={this.handleProductSelection}
        />
      );
    }
    return <GameLoader selectedActivity={this.props.selectedActivity} />;
  }

  render() {
    return (
      <View style={styles.window} ref={ref => { this.screenWrapperRef = ref; }}>
        <AppGlobalStyle />
        <GlobalErrorHandler store={globalStore}>
          <AmpGlobalNav />
          <Title />
          {this.props.isCaptionsEnabled && <Captions className="global-captions" />}
          <div id="gameloader-wrapper" ref={(ref) => { this.gameLoaderWrapperRef = ref; }}>
            {this.state.canRenderMainContent && this.renderMainContent()}
          </div>
          <ModalSystem />
          <SessionTimeout />
          <CustomErrorThrower />
          <LiveRegionContainer />
          {(
            this.props.isWriteResultInProgress ||
            this.props.isReportingMultipleSessions
          ) && (
            <LoadingWrapper>
              <LoadingScreen animationType={ANIMATION_TYPE.SPINNER} noAudioUnload />
            </LoadingWrapper>
          )}
        </GlobalErrorHandler>
      </View>
    );
  }
}

ActivityComponent.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  selectedActivity: PropTypes.string.isRequired,
  season: PropTypes.string.isRequired,
  openLogoutModal: PropTypes.func.isRequired,
  openMultipleSessionsModal: PropTypes.func.isRequired,
  fetchTFEConfig: PropTypes.func.isRequired,
  fetchVocabResultsData: PropTypes.func.isRequired,
  getAllGamesState: PropTypes.func.isRequired,
  fetchVocabStatus: PropTypes.func.isRequired,
  fetchFluencyAssignments: PropTypes.func.isRequired,
  setQuestSessionId: PropTypes.func.isRequired,
  transformPlacementFields: PropTypes.func.isRequired,
  updateTOY: PropTypes.func.isRequired,
  getFeatureToggles: PropTypes.func.isRequired,
  getEMGames: PropTypes.func.isRequired,
  fetchSessionProgress: PropTypes.func.isRequired,
  isCaptionsEnabled: PropTypes.bool.isRequired,
  setSkillScanRedirectFlag: PropTypes.func.isRequired,
  setBenchmarkModeRedirectFlag: PropTypes.func.isRequired,
  isWriteResultInProgress: PropTypes.bool.isRequired,
  isReportingMultipleSessions: PropTypes.bool.isRequired,
  setProduct: PropTypes.func.isRequired,
  setShowGamePrototypes: PropTypes.func.isRequired,
  productsAvailable: PropTypes.array.isRequired,
  productSelected: PropTypes.string.isRequired,
  setLocalizedThunk: PropTypes.func.isRequired,
  setInitialQuestStateForLectura: PropTypes.func.isRequired,
  handleDeepLinkParams: PropTypes.func.isRequired,
  getDemoData: PropTypes.func.isRequired,
  apiVersionKeycloak: PropTypes.string.isRequired,
  isStaffLogin: PropTypes.bool.isRequired,
  keycloakSessionId: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
  openInitialActivity: PropTypes.func.isRequired,
  reportAppLoaded: PropTypes.func.isRequired,
  studentLoginEventHandler: PropTypes.func.isRequired,
  fetchProductsWeeklyUsage: PropTypes.func.isRequired,
  fetchUsageTargets: PropTypes.func.isRequired,
  currentQuestId: PropTypes.number.isRequired,
  reportRumView: PropTypes.func.isRequired,
  isGlobalError: PropTypes.bool.isRequired,
  deepLinkConfig: PropTypes.object.isRequired,
  hasPrototypeParam: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  isModalOpen: getIsAquaModalOpen(state),
  selectedActivity: state.selectedActivity.activity,
  season: state.questWorkflowReducer.season,
  isCaptionsEnabled: getIsClientA11yFeatures(state) && getAreCaptionsAvailable(state),
  studentSettings: state.questWorkflowReducer.gamesData.STUDENT_SETTINGS,
  isWriteResultInProgress: state.progressScreen.isWriteResultInProgress,
  isReportingMultipleSessions: getIsReportingMultipleSessions(state),
  productsAvailable: getAuthDataProductsAvailable(state),
  productSelected: state.appSessionReducer.product,
  currentQuestId: getCurrentQuestId(state),
  isGlobalError: state.globalErrorReducer.hasError,
});

const mapDispatchToProps = {
  transformPlacementFields,
  openLogoutModal,
  openMultipleSessionsModal,
  openInitialActivity,
  getAllGamesState,
  fetchVocabStatus,
  fetchTFEConfig,
  fetchFluencyAssignments,
  fetchVocabResultsData,
  getFeatureToggles,
  getEMGames,
  updateTOY,
  setQuestSessionId,
  fetchSessionProgress,
  setSkillScanRedirectFlag,
  setBenchmarkModeRedirectFlag,
  setProduct,
  setShowGamePrototypes,
  setLocalizedThunk,
  setInitialQuestStateForLectura,
  handleDeepLinkParams,
  getDemoData,
  studentLoginEventHandler,
  fetchProductsWeeklyUsage,
  fetchUsageTargets,
  reportAppLoaded: () => ({ type: 'REPORT_APP_LOADED' }),
  reportRumView,
};

export default connect(mapStateToProps, mapDispatchToProps)(ActivityComponent);

const styles = StyleSheet.create({
  window: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-around',
    overflow: 'hidden',
    alignItems: 'center',
    width: 1024,
    height: 576,
    backgroundColor: '#001d1b',
  },
  popupContainer: {
    position: 'absolute',
    width: 1024,
    height: 576,
  },
});

const LoadingWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-color: rgb(255, 255, 255, 0.3);
  z-index: 9999;
`;
